import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, Droplets, Flame, Gauge, Filter } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Categories match the 3 main services ONLY
const categories = [
  { name: 'All', slug: 'all', icon: Filter },
  { name: 'Plumbing', slug: 'Plumbing', icon: Droplets },
  { name: 'Heating', slug: 'Heating', icon: Flame },
  { name: 'Gas Fitting', slug: 'Gas Fitting', icon: Gauge },
];

// All blog posts with properly matched images for their topic
const blogPosts = [
  {
    id: 'tank-vs-tankless-water-heater',
    title: 'Tank vs Tankless Water Heater: Which Is Right For You?',
    excerpt: 'Comparing traditional tank and tankless water heaters. Learn about costs, efficiency, and which option makes sense for your Calgary home.',
    image: '/images/dnb/blog-tank-vs-tankless.jpg',
    imageAlt: 'Tank vs tankless water heater comparison for Calgary homes by DNB',
    category: 'Plumbing',
    readTime: '6 min read',
    date: 'January 15, 2026',
    relatedService: '/plumbing/tankless-water-heater-calgary'
  },
  {
    id: 'should-you-replace-polyb',
    title: 'Should You Replace Poly-B Plumbing? Insurance & Safety Concerns',
    excerpt: 'If your Calgary home was built between 1978-1995, you may have Poly-B pipes. Learn about insurance implications and why replacement is recommended.',
    image: '/images/dnb/blog-polyb.jpg',
    imageAlt: 'Calgary Poly-B replacement with new PEX piping by DNB',
    category: 'Plumbing',
    readTime: '7 min read',
    date: 'January 12, 2026',
    relatedService: '/plumbing/poly-b-replacement-calgary'
  },
  {
    id: 'furnace-heat-exchanger-dangers',
    title: 'Cracked Heat Exchanger: The Hidden Danger in Your Furnace',
    excerpt: 'Learn about the dangers of a cracked heat exchanger, carbon monoxide risks, and why regular furnace inspections are essential for your family\'s safety.',
    image: '/images/dnb/blog-heat-exchanger.jpg',
    imageAlt: 'Furnace heat exchanger inspection in a Calgary home by DNB',
    category: 'Heating',
    readTime: '6 min read',
    date: 'January 10, 2026',
    relatedService: '/heating/furnace-installation-repair-calgary'
  },
  {
    id: 'garage-heater-installation-guide',
    title: 'Garage Heater Installation Guide for Calgary Homeowners',
    excerpt: 'Everything you need to know about installing a garage heater in Calgary. Types, costs, gas line requirements, and benefits explained.',
    image: '/images/dnb/blog-garage-heater.jpg',
    imageAlt: 'Natural gas garage heater installed in a Calgary garage by DNB',
    category: 'Heating',
    readTime: '6 min read',
    date: 'January 8, 2026',
    relatedService: '/heating/garage-heaters-calgary'
  },
  {
    id: 'signs-furnace-failing',
    title: 'Signs Your Furnace Is Failing: When to Repair or Replace',
    excerpt: 'Don\'t wait for a complete breakdown. Learn the warning signs that your furnace needs repair or replacement before Calgary\'s cold sets in.',
    image: '/images/dnb/blog-furnace-failing.jpg',
    imageAlt: 'Calgary furnace diagnosis and repair by DNB',
    category: 'Heating',
    readTime: '5 min read',
    date: 'January 5, 2026',
    relatedService: '/heating/furnace-installation-repair-calgary'
  },
  {
    id: 'backflow-testing-calgary',
    title: 'Backflow Testing in Calgary: What Homeowners Need to Know',
    excerpt: 'Understanding City of Calgary backflow testing requirements, why it matters, and how to stay compliant with annual testing.',
    image: '/images/dnb/blog-backflow.jpg',
    imageAlt: 'Certified backflow testing on a Calgary property by DNB',
    category: 'Plumbing',
    readTime: '5 min read',
    date: 'January 3, 2026',
    relatedService: '/plumbing/backflow-testing-calgary'
  },
  {
    id: 'preventing-frozen-pipes-calgary',
    title: 'Preventing Frozen Pipes in Calgary Winters',
    excerpt: 'Essential tips to protect your plumbing from Calgary\'s freezing temperatures and prevent costly water damage.',
    image: '/images/dnb/blog-frozen-pipes.jpg',
    imageAlt: 'Calgary winter pipe protection and frozen pipe prevention by DNB',
    category: 'Plumbing',
    readTime: '4 min read',
    date: 'December 28, 2025',
    relatedService: '/plumbing/plumbing-repairs-calgary'
  },
  {
    id: 'water-heater-maintenance-tips',
    title: 'Water Heater Care Tips to Extend Equipment Life',
    excerpt: 'Simple care tasks that can extend your water heater\'s life and improve efficiency. Save money with these DIY tips.',
    image: '/images/dnb/blog-water-heater-maintenance.jpg',
    imageAlt: 'Calgary water heater service and inspection by DNB',
    category: 'Plumbing',
    readTime: '4 min read',
    date: 'December 25, 2025',
    relatedService: '/plumbing/water-heater-replacement-calgary'
  },
  {
    id: 'bbq-gas-line-installation',
    title: 'BBQ Gas Line Installation: What Calgary Homeowners Should Know',
    excerpt: 'Thinking about installing a natural gas line for your BBQ? Here\'s what\'s involved, permits required, and why it\'s worth the investment.',
    image: '/images/dnb/blog-bbq-gas.jpg',
    imageAlt: 'Permanent natural gas BBQ line installed by DNB in Calgary',
    category: 'Gas Fitting',
    readTime: '4 min read',
    date: 'December 20, 2025',
    relatedService: '/gas-fitting/bbq-gas-lines-calgary'
  },
  {
    id: 'hydronic-heating-calgary',
    title: 'Hydronic Heating Systems: A Guide for Calgary Homeowners',
    excerpt: 'Many Calgary homes have hydronic (boiler) heating. Learn how these systems work, common problems, and service tips.',
    image: '/images/dnb/blog-hydronic.jpg',
    imageAlt: 'Calgary hydronic boiler and radiant heating system serviced by DNB',
    category: 'Heating',
    readTime: '6 min read',
    date: 'December 15, 2025',
    relatedService: '/heating/boiler-hydronic-calgary'
  },
  {
    id: 'drain-cleaning-tips',
    title: 'How to Prevent Clogged Drains: Tips from Calgary Plumbers',
    excerpt: 'Simple habits to keep your drains flowing freely. Learn what causes clogs and when to call a professional.',
    image: '/images/dnb/blog-clogged-drains.jpg',
    imageAlt: 'Calgary drain cleaning and clog prevention by DNB',
    category: 'Plumbing',
    readTime: '4 min read',
    date: 'December 10, 2025',
    relatedService: '/plumbing/drain-cleaning-calgary'
  },
  {
    id: 'water-softener-benefits',
    title: 'Do You Need a Water Softener in Calgary?',
    excerpt: 'Calgary has hard water. Learn how a water softener can protect your plumbing, appliances, and improve water quality.',
    image: '/images/dnb/blog-water-softener.jpg',
    imageAlt: 'Whole-home water softener installed by DNB in Calgary',
    category: 'Plumbing',
    readTime: '5 min read',
    date: 'December 5, 2025',
    relatedService: '/plumbing/water-softeners-calgary'
  },
  {
    id: 'gas-fireplace-safety',
    title: 'Gas Fireplace Safety: What Every Homeowner Should Know',
    excerpt: 'Gas fireplaces require regular service and safety checks. Learn how to enjoy your fireplace safely this winter.',
    image: '/images/dnb/blog-gas-fireplace.jpg',
    imageAlt: 'Calgary gas fireplace connection and safety check by DNB',
    category: 'Gas Fitting',
    readTime: '5 min read',
    date: 'November 25, 2025',
    relatedService: '/gas-fitting/gas-fireplace-calgary'
  },
  {
    id: 'smart-thermostat-benefits',
    title: 'Smart Thermostat Benefits: Save Energy and Money',
    excerpt: 'A smart thermostat can reduce your heating costs by 10-15%. Learn about the benefits and best options for Calgary homes.',
    image: '/images/dnb/blog-thermostat.jpg',
    imageAlt: 'Smart thermostat installed in a Calgary home by DNB',
    category: 'Heating',
    readTime: '4 min read',
    date: 'November 20, 2025',
    relatedService: '/heating/thermostats-controls-calgary'
  }
];

export const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>Expert Tips | Plumbing & Heating Advice | DNB Calgary</title>
        <meta name="description" content="Expert plumbing, heating, and gas fitting tips for Calgary homeowners. Learn troubleshooting guides, care tips, and when to call a professional." />
      </Helmet>

      <main data-testid="blog-page" className="page-transition">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#111827] to-[#0F172A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className="text-[#0EA5E9] uppercase tracking-widest text-sm font-semibold">Expert Tips</span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white mt-4 mb-6">
                Tips & Advice for Calgary Homeowners
              </h1>
              <p className="text-white/80 max-w-2xl mx-auto">
                Expert advice on plumbing, heating, and gas fitting. Learn how to maintain your home&rsquo;s systems and make informed decisions.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-6 bg-white border-b border-slate-200 sticky top-[120px] z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 -mb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  data-testid={`category-${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === cat.slug
                      ? 'bg-[#0F172A] text-white'
                      : 'bg-slate-50 text-slate-600 hover:bg-gray-200'
                  }`}
                >
                  <cat.icon className="w-4 h-4" />
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-16 section-tint">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600">No articles found in this category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`blog-post-${post.id}`}
                    className="bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200 group hover:shadow-xl transition-shadow"
                  >
                    <Link to={`/blog/${post.id}`}>
                      <div className="aspect-[16/10] overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.imageAlt || post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                          <span className="px-2 py-1 bg-[#0F172A]/10 text-[#0F172A] rounded font-medium">
                            {post.category}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {post.readTime}
                          </span>
                        </div>
                        <h2 className="font-heading font-bold text-xl text-[#0F172A] mb-3 group-hover:text-[#0F172A] transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Calendar className="w-4 h-4" />
                            {post.date}
                          </span>
                          <span className="text-[#0F172A] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read More
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#0F172A] mb-4">
                Have a Question About Your Home's Systems?
              </h2>
              <p className="text-slate-600 mb-6">
                Our certified technicians are happy to help. Contact us for expert advice.
              </p>
              <Link
                to="/contact"
                className="bg-[#0284C7] hover:bg-[#0369A1] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
};

export default BlogPage;
